import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { loadStripe as initStripe } from "@stripe/stripe-js";

// Define subscription plans
export const SUBSCRIPTION_PLANS = {
  PREMIUM: {
    id: "price_1R87jM4fRhCB9zfKugZGXbi6", // Updated with a real price ID
    name: "Premium Plan",
    price: 14.99,
    features: [
      "Unlimited access to all content",
      "No ads",
      "Support quality journalism",
      "Early access to new features"
    ]
  },
  CUSTOM: {
    id: "custom",
    name: "Journalist Subscription",
    features: [
      "Support your favorite journalists directly",
      "Access to premium content from specific journalists",
      "Private Q&A sessions",
      "Behind-the-scenes content"
    ]
  }
};

// Initialize Stripe with publishable key from environment variable
let stripePromise: Promise<any> | null = null;

// Load the Stripe.js library
export const loadStripe = () => {
  if (!stripePromise) {
    stripePromise = initStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Fetch all active Stripe products and their prices
export const fetchStripeProducts = async () => {
  try {
    // This is now done via an edge function or server request since direct Stripe API calls 
    // can't be made from the browser due to API key security
    const { data, error } = await supabase.functions.invoke('get-stripe-products');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching Stripe products:', error);
    toast({
      title: 'Error',
      description: 'Could not fetch subscription plans',
      variant: 'destructive'
    });
    return [];
  }
};

// Get user's current subscription status
export const getUserSubscriptionStatus = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { 
        hasActiveSubscription: false, 
        subscriptionDetails: null 
      };
    }

    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .single();

    if (error) {
      console.error('Error fetching subscription:', error);
      return { 
        hasActiveSubscription: false, 
        subscriptionDetails: null 
      };
    }

    return { 
      hasActiveSubscription: !!data, 
      subscriptionDetails: data 
    };
  } catch (error) {
    console.error('Error checking subscription:', error);
    return { 
      hasActiveSubscription: false, 
      subscriptionDetails: null 
    };
  }
};

// Create a checkout session for platform subscription
export const createCheckoutSession = async (priceId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: { priceId }
    });

    if (error) throw error;
    
    return data?.url || null;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    toast({
      title: 'Checkout Error',
      description: 'Could not create checkout session',
      variant: 'destructive'
    });
    return null;
  }
};

// Create checkout session for journalist subscription
export const createJournalistCheckoutSession = async (journalistId: string, priceId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: { 
        priceId,
        journalistId,
        isJournalistSubscription: true
      }
    });

    if (error) throw error;
    
    return data?.url || null;
  } catch (error) {
    console.error('Error creating journalist checkout session:', error);
    toast({
      title: 'Checkout Error',
      description: 'Could not create checkout session for journalist subscription',
      variant: 'destructive'
    });
    return null;
  }
};

// Get subscription status wrapper for Subscription page
export const getSubscriptionStatus = async () => {
  const { hasActiveSubscription, subscriptionDetails } = await getUserSubscriptionStatus();
  return {
    isSubscribed: hasActiveSubscription,
    details: subscriptionDetails
  };
};

// Get wallet balance
export const getWalletBalance = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { balance: 0 };
    }

    const { data, error } = await supabase
      .from('user_wallet')
      .select('balance')
      .eq('user_id', user.id)
      .single();

    if (error || !data) {
      console.error('Error fetching wallet balance:', error);
      return { balance: 0 };
    }

    return { balance: data.balance };
  } catch (error) {
    console.error('Error fetching wallet balance:', error);
    return { balance: 0 };
  }
};

// Top up wallet
export const topUpWallet = async (amount: number) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }

    const { data, error } = await supabase.functions.invoke('top-up-wallet', {
      body: { amount }
    });
    
    if (error) throw error;
    
    return data || { success: false, error: 'Unknown error' };
  } catch (error) {
    console.error('Error topping up wallet:', error);
    return { success: false, error: 'Failed to top up wallet' };
  }
};

// Create customer portal session
export const createCustomerPortalSession = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }

    const { data, error } = await supabase.functions.invoke('create-customer-portal');
    
    if (error) throw error;
    
    return data?.url || null;
  } catch (error) {
    console.error('Error creating customer portal session:', error);
    toast({
      title: 'Portal Error',
      description: 'Could not access customer portal',
      variant: 'destructive'
    });
    return null;
  }
};

// Journalist specific functions
export const createJournalistOnboarding = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }
    
    const { data, error } = await supabase.functions.invoke('create-journalist-onboarding');
    
    if (error) throw error;
    
    return data?.url || null;
  } catch (error) {
    console.error('Error creating journalist onboarding:', error);
    toast({
      title: 'Onboarding Error',
      description: 'Could not create Stripe Connect account',
      variant: 'destructive'
    });
    return null;
  }
};

export const getJournalistDashboard = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }
    
    const { data, error } = await supabase.functions.invoke('get-journalist-dashboard');
    
    if (error) throw error;
    
    return data?.url || null;
  } catch (error) {
    console.error('Error accessing journalist dashboard:', error);
    toast({
      title: 'Dashboard Error',
      description: 'Could not access Stripe dashboard',
      variant: 'destructive'
    });
    return null;
  }
};

// Purchase article (micropayment)
export const purchaseArticle = async (articleId: string, amount: number) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }

    const { data, error } = await supabase.functions.invoke('purchase-article', {
      body: { articleId, amount }
    });

    if (error) throw error;
    
    return data || { success: false, error: 'Unknown error' };
  } catch (error) {
    console.error('Error purchasing article:', error);
    toast({
      title: 'Purchase Error',
      description: 'Failed to purchase article',
      variant: 'destructive'
    });
    return { success: false, error: 'Failed to purchase article' };
  }
};

// Check if user has access to a specific journalist's content
export const checkJournalistSubscription = async (journalistId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { hasAccess: false };
    }

    const { data, error } = await supabase
      .from('journalist_subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .eq('journalist_id', journalistId)
      .eq('status', 'active')
      .single();

    if (error) {
      console.error('Error checking journalist subscription:', error);
      return { hasAccess: false };
    }

    return { hasAccess: !!data, subscriptionDetails: data };
  } catch (error) {
    console.error('Error checking journalist subscription:', error);
    return { hasAccess: false };
  }
};
